import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { AppService, AppSetting, DCO, DentalBidirectionalListItem, DentalReferralForm, DentalReferralFormReview, GetDentalBidirectionalListsRequest, GetDentalReferralsByUserRequest, GetDentalReferralsByUserResponse, GetRuntimeAppSettingsRequest, GetUserDcoRequest, HRATReferral, MaternityReferral, PCPReferral, RuntimeAppSetting, SaveDentalReferralReviewRequest, SaveDentalReferralReviewsRequest } from '../app.service';
import { AlertService } from '../common/alert.service';
import { LoadingService } from '../common/loading.service';
import { MatDialog } from '@angular/material/dialog';
import { catchError, finalize, map, Observable, of, tap, throwError } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { DcoReferralDetailsDialogComponent } from './dco-referral-details-dialog/dco-referral-details-dialog.component';
import { ReprocessRequestDialogComponent } from '../common/reprocess-request-dialog/reprocess-request-dialog.component';
import { MatSort, Sort } from '@angular/material/sort';
import { CommonFunctions } from '../common/common-functions';
import { MatPaginator } from '@angular/material/paginator';
import { DatePipe } from '@angular/common';
import { ReferralLockService } from '../common/referral-lock.service';
import { AuthService } from '../auth/services/auth.service';
import { ConfirmDialogComponent } from '../common/confirm-dialog/confirm-dialog.component';
import { ExportDialogComponent } from '../common/export/export-dialog/export-dialog.component';
import type { ExportDialogData } from '../common/export/models';


@Component({
  selector: 'app-dco-referrals',
  templateUrl: './dco-referrals.component.html',
  styleUrl: './dco-referrals.component.scss'
})
export class DcoReferralsComponent implements OnInit, AfterViewInit {

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  public referrals: DentalReferralForm[] = [];
  displayedColumns: string[] = ['select', 'locked', 'memberId', 'memberName', 'parent', 'phone', 'receivedDate', 'source', 'spoke', 'message', 'letter', 'apptDate', 'dcoNote', 'actions'];
  referralsDataSource: MatTableDataSource<DentalReferralForm> = new MatTableDataSource([]);

  trackById = (_: number, r: DentalReferralForm) => r.id;  

  // Initialize a SelectionModel to track the selected rows
  selection = new SelectionModel<DentalReferralForm>(true, []); // true for multi-selection

  apptDatePicker: any;
  selectedReferral: DentalReferralForm = null;

  private lockSortSnapshot = new Map<number, 0 | 1>();

  private lastSort: Sort | null = null;

  public userAccountDco: DCO = null;

  //activeRow: number | null = null; // Tracks the active row index
  isDatePickerChanged: boolean = false; // Tracks if the datepicker was used
  showClosed: boolean = false; // Default to show all referrals

  dcoDisplayName: string = '';
  public urgentOnly = false;

  public dcoNotesListItems: DentalBidirectionalListItem[] = [];
  
  public currentSearchValue: string = '';
  public currentFilterSelection: string = '';
  public currentActionValue: string = '';
  public filtersEnabled: boolean = true;

  constructor(
    private service: AppService,
    private alert: AlertService,
    private spinner: LoadingService,
    private dialog: MatDialog,
    private commonFunctions: CommonFunctions,
    private datePipe: DatePipe,
    private referralLockService: ReferralLockService,
    private authService: AuthService
  ) { }


  ngOnInit(): void {

    this.loadLists();
    // Reset the dropdown after the action is performed
    this.currentActionValue = '';


    this.getDentalReferrals().subscribe(() => {
      this.referrals.forEach(referral => {
        if (!referral.review) {
          referral.review = new DentalReferralFormReview();
        }
      });

      this.referralsDataSource.filterPredicate = (data: DentalReferralForm, combinedFilter: string) => {
        const [searchText, filterSelection, urgentFlag] = combinedFilter.split('::');
        const normalizedSearch = (searchText || '').toLowerCase().trim();
        const numericSearch = normalizedSearch.replace(/\D/g, '');
        const isNumericOnly = /^[0-9]+$/.test(normalizedSearch);

        const normalizedPhone = (data.memberPhoneNumber || '').replace(/\D/g, '');
        let passesSearch = true;

        if (normalizedSearch.length >= 3) {
          if (isNumericOnly || normalizedSearch.match(/[\(\)\-\s]/)) {
            passesSearch = normalizedPhone.includes(numericSearch);
          } else {
            passesSearch =
              (data.memberMedicaidId?.toLowerCase().includes(normalizedSearch) ?? false) ||
              (data.memberFirstName?.toLowerCase().includes(normalizedSearch) ?? false) ||
              (data.memberLastName?.toLowerCase().includes(normalizedSearch) ?? false) ||
              (data.review?.dcoNote?.toLowerCase().includes(normalizedSearch) ?? false);
          }
        }

        // Apply dropdown filter
        let passesDropdown = true;
        if (filterSelection) {
          if (filterSelection.startsWith('NOTE:')) {
            const noteVal = filterSelection.substring(5); // everything after "NOTE:"
            const current = data.review?.dcoNote ?? '';
            passesDropdown = current === noteVal;
          } else if (filterSelection === 'STATUS:CLOSED') {
            passesDropdown = data.review?.isClosed === true;
          } else if (filterSelection === 'STATUS:OPEN') {
            passesDropdown = data.review?.isClosed !== true; // treat undefined as open
          } else {
            switch (filterSelection) {
              case 'PCP':
                passesDropdown = (data as any).referralType === 'PCP';
                break;
              case 'HRAT':
                passesDropdown = (data as any).referralType === 'HRAT';
                break;
              case 'Maternity':
                passesDropdown = (data as any).referralType === 'Maternity';
                break;
              case 'HasSpokeWith':
                passesDropdown = data.review?.spokeWithMember === true;
                break;
              case 'HasLeftMessage':
                passesDropdown = data.review?.leftMessage === true;
                break;
              case 'HasSentLetter':
                passesDropdown = data.review?.sentLetter === true;
                break;
              case 'HasScheduledAppointment':
                passesDropdown = data.review?.scheduledAppointment === true;
                break;
              case 'HasNotes':
                passesDropdown = !!(data.review?.dcoNote && data.review?.dcoNote.trim().length > 0);
                break;
            }
          }
        }

        // Urgent gate (independent of the dropdown)
        const wantUrgentOnly = urgentFlag === '1';
        const passesUrgent = !wantUrgentOnly || this.isUrgent(data);

        return passesSearch && passesDropdown && passesUrgent;
      };




      // Clear any existing selection to ensure no rows are selected on load
      this.selection.clear();
    });

  }

  ngAfterViewInit() {
    this.paginator.pageSize = 10; //set default row count
    this.referralsDataSource.paginator = this.paginator;
    this.referralsDataSource.sort = this.sort;
  }

  private loadLists() {
    const request = new GetDentalBidirectionalListsRequest();

    // Show the spinner before the request starts
    this.spinner.show();

    this.service.GetDentalBidirectionalLists(request).pipe(
      tap((result) => {
        if (result.success) {
          this.dcoNotesListItems = result.lists.find(l => l.name == 'DCONotes').listItems.filter(i => i.active == true);
        } else {
          console.log(`ERROR: ${result.message}`);
        }
      }),
      catchError((error) => {
        console.error(error);
        return of([]); // Return an empty array or appropriate fallback in case of error
      }),
      finalize(() => {
        // Hide the spinner after the request completes, whether it was successful or not
        this.spinner.hide();
      })
    ).subscribe();
  }

  private sortReferrals(arr: DentalReferralForm[], sortState: Sort): DentalReferralForm[] {
    const isAsc = sortState.direction === 'asc';
    return arr.slice().sort((a, b) => {
      switch (sortState.active) {
        case 'memberId':
          return this.commonFunctions.compare(a.memberMedicaidId, b.memberMedicaidId, isAsc);
        case 'memberName':
          return this.commonFunctions.compare(a.memberFirstName, b.memberFirstName, isAsc);
        case 'parent':
          return this.commonFunctions.compare(a.memberParentOrGuardianName, b.memberParentOrGuardianName, isAsc);
        case 'phone': {
          const da = a.memberPhoneNumber?.replace(/\D/g, '') || '';
          const db = b.memberPhoneNumber?.replace(/\D/g, '') || '';
          return this.commonFunctions.compare(Number(da), Number(db), isAsc);
        }
        case 'receivedDate': {
          const da = a.receivedDate ? new Date(a.receivedDate) : undefined;
          const db = b.receivedDate ? new Date(b.receivedDate) : undefined;
          if (!da && !db) return 0;
          if (!da) return isAsc ? 1 : -1;
          if (!db) return isAsc ? -1 : 1;
          return this.commonFunctions.compare(da, db, isAsc);
        }
        case 'source':
          return this.commonFunctions.compare(a.referralType, b.referralType, isAsc);
        case 'spoke':
          return this.commonFunctions.compare(a.review?.spokeWithMember, b.review?.spokeWithMember, isAsc);
        case 'message':
          return this.commonFunctions.compare(a.review?.leftMessage, b.review?.leftMessage, isAsc);
        case 'letter':
          return this.commonFunctions.compare(a.review?.sentLetter, b.review?.sentLetter, isAsc);
        case 'apptDate':
          return this.commonFunctions.compare(new Date(a.review?.appointmentDate), new Date(b.review?.appointmentDate), isAsc);
        case 'dcoNote':
          return this.commonFunctions.compare(a.review?.dcoNote, b.review?.dcoNote, isAsc);
        //case 'locked':
        //  return this.commonFunctions.compare(a.review?.isLocked, b.review?.isLocked, isAsc);
        case 'locked': {
          // If we're currently editing a row, keep its PRE-EDIT lock value for sorting
          const va = this.lockSortSnapshot.has(a.id)
            ? this.lockSortSnapshot.get(a.id)!
            : (a.review?.isLocked ? 1 : 0);
          const vb = this.lockSortSnapshot.has(b.id)
            ? this.lockSortSnapshot.get(b.id)!
            : (b.review?.isLocked ? 1 : 0);
          return this.commonFunctions.compare(va, vb, isAsc);
        }
        default:
          return 0;
      }
    });
  }

  private rebindPreservingState(): void {
    const prevPageIdx = this.paginator?.pageIndex ?? 0;
    const prevPageSz = this.paginator?.pageSize ?? 10;

    // Start from the backing array
    let data = [...this.referrals];

    // Reapply last sort (if any)
    if (this.lastSort && this.lastSort.direction) {
      data = this.sortReferrals(data, this.lastSort);
    }

    data = this.dedupeById(data);

    // Rebind
    this.referralsDataSource.data = data;
    this.referralsDataSource.paginator = this.paginator;
    this.referralsDataSource.sort = this.sort;

    // Reapply current filter
    this.applyFilter();
    this.clampSelectionToFilter();

    // Restore page
    if (this.paginator) {
      this.paginator.pageIndex = prevPageIdx;
      this.paginator._changePageSize(prevPageSz);
    }
  }

  private getDentalReferrals(): Observable<DentalReferralForm[]> {
    const request = new GetDentalReferralsByUserRequest();
    request.includeClosedReferrals = this.showClosed;
    
    // Show the spinner before the request starts
    this.spinner.show();

    return this.service.GetDentalReferralsByUser(request).pipe(
      map((result: GetDentalReferralsByUserResponse) => {
       
        if (result.success) {

          const sortedReferrals = result.referrals.sort((a, b) => {
            const dateA = a.receivedDate ? new Date(a.receivedDate).getTime() : 0;
            const dateB = b.receivedDate ? new Date(b.receivedDate).getTime() : 0;
            return dateB - dateA; // Descending
          });


          this.referrals = this.dedupeById(sortedReferrals);
          this.dcoDisplayName = result.dcoDisplayName;

          this.referrals.forEach(referral => {
            if (!referral.review) {
              referral.review = new DentalReferralFormReview(); // Initialize as an empty object if undefined
            }
          });
          
          this.referralsDataSource.data = [...this.referrals]; // Update data property only
          
          return result.referrals; // Return the DCO array
        } else {
          console.log(`ERROR: ${result.message}`);
          this.alert.error(`Error: ${result.message}`);
          return []; // Return an empty array in case of failure
        }
      }),
      catchError((error) => {
        console.error(error);
        return of([]); // Return an empty array or appropriate fallback in case of error
      }),
      finalize(() => {
        // Hide the spinner after the request completes
        this.spinner.hide();
      })
    );
  }

  activateRow(referral: DentalReferralForm, index: number) {
    if (referral.review?.isClosed) {
      // Prevent activation if the referral is closed
      return;
    }

    if (!this.selectedReferral) {
      this.isDatePickerChanged = false;
      this.selectedReferral = JSON.parse(JSON.stringify(referral));
      //this.activeRow = this.activeRow === index ? null : index; // Toggle the active row
    }
  }

  // Master toggle to select or deselect all *filtered* rows
  masterToggle() {
    const filtered = this.getFilteredRows();
    const allSelected = filtered.length > 0 && filtered.every(r => this.selection.isSelected(r));

    if (allSelected) {
      // Deselect only filtered rows
      filtered.forEach(r => this.selection.deselect(r));
    } else {
      // Select only filtered rows
      filtered.forEach(r => this.selection.select(r));
    }
  }

  isAllSelected(): boolean {
    const filtered = this.getFilteredRows();
    if (filtered.length === 0) return false;
    return filtered.every(r => this.selection.isSelected(r));
  }

  isIndeterminate(): boolean {
    const filtered = this.getFilteredRows();
    if (filtered.length === 0) return false;
    const selectedInFiltered = filtered.filter(r => this.selection.isSelected(r)).length;
    return selectedInFiltered > 0 && selectedInFiltered < filtered.length;
  }

  // Check if the master checkbox should be checked
  checkboxLabel(row?: DentalReferralForm): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.memberMedicaidId}`;
  }

  // Method to handle edit action
  onEdit(referral: DentalReferralForm, index: number): void {
    const request = { referralId: referral.id, referralType: referral.referralType };

    this.referralLockService.LockReferral(request).subscribe({
      next: (res) => {
        const prevIdx = this.referrals.findIndex(r => r.id === referral.id);


        const lockedReferral = res.referral ?? referral;
        const currentUserId = this.authService.getUserId()?.toLowerCase();
        const lockedByUserId = lockedReferral.review?.lockedByUserId?.toLowerCase();

        if (res.success && res.referral && prevIdx !== -1) {
          if (this.lastSort?.active === 'locked') {
            const wasLocked = this.referrals[prevIdx]?.review?.isLocked ? 1 : 0;
            this.lockSortSnapshot.set(referral.id, wasLocked);
          }

          this.referrals[prevIdx] = res.referral;
          this.referrals = this.dedupeById([...this.referrals]);
          this.rebindPreservingState();

          const displayedIndex = this.referralsDataSource.data.findIndex(r => r.id === referral.id);
          if (displayedIndex !== -1) {
            this.activateRow(this.referralsDataSource.data[displayedIndex], displayedIndex);
          }
        } else if (lockedReferral.review?.isLocked && lockedByUserId === currentUserId) {
          const i = this.referrals.findIndex(r => r.id === referral.id);
          if (i !== -1) this.activateRow(this.referrals[i], i);
        } else {
          const lockedBy = lockedReferral.review?.lockedByUser?.fullName ?? 'another user';
          const formattedDate = this.datePipe.transform(lockedReferral.review?.lockedDate, 'MM/dd/yyyy h:mm a');
          this.alert.warning(`This referral was locked by ${lockedBy} on ${formattedDate}.`);
        }
      },
      error: () => this.alert.error('An unexpected error occurred while attempting to lock the referral.')
    });
  }



  // Method to handle clipboard action
  onReProcessAction(referral: DentalReferralForm, index: number) {
    if (referral.review?.isLocked) {
      const lockedBy = referral.review?.lockedByUser?.fullName;
      const formattedDate = this.datePipe.transform(referral.review?.lockedDate, 'MM/dd/yyyy h:mm a');
      this.alert.warning(`This referral cannot be sent for reprocessing because it is currently locked by ${lockedBy} on ${formattedDate}.`);
      return;
    }
    this.openReprocessReferralDialog(referral);
  }

  // Method to handle info action
  onInfo(referral: DentalReferralForm, index: number) {
    const request = {
      referralId: referral.id,
      referralType: referral.referralType
    };

    this.service.GetReferralByReferralId(request).subscribe({
      next: (response) => {
        if (response.success && response.referral) {
          this.openReferralDetailsDialog(response.referral);
        } else {
          this.alert.warning('Unable to retrieve the latest referral details.');
        }
      },
      error: (err) => {
        console.error('Error fetching referral:', err);
        this.alert.error('An error occurred while fetching referral details.');
      }
    });
  }

  // Method to handle save action
  onSave(referral: DentalReferralForm, index: number) {
    
    // Set isClosed to true if the datepicker was explicitly used and a date exists
    if (this.isDatePickerChanged && referral.review.appointmentDate) {
      referral.review.isClosed = true;
    }

    // If the appointment date is cleared, set isClosed to false
    if (!referral.review.appointmentDate) {
      referral.review.isClosed = false;
    }

    // Reset the flag after save
    this.isDatePickerChanged = false;

    this.spinner.show();
    const request = new SaveDentalReferralReviewRequest();
    request.review = referral.review;
    request.review.referralId = referral.id;
    request.review.referralType = referral.referralType;

    console.log(`request review id: ${request?.review.id}`);
    console.log(`request referral id: ${request?.review.referralId}`);
    console.log(`request referral type: ${request?.review.referralType}`);

    this.service.SaveDentalReferralReview(request)
      .pipe(
        finalize(() => {
          // Hide the spinner after the request completes
          this.spinner.hide();
        })
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.alert.success('Referral updated successfully!');

            this.reloadDentalReferralGrid();
            
          } else {
            this.alert.warning(response.message);
          }
        },
        error: (error) => {
          console.error('Error submitting form:', error);
          this.alert.error('Failed to submit the form. Please try again.');
        }
      });

    // Deactivate the row after saving
    //this.activeRow = null;
    this.selectedReferral = null;
    this.lockSortSnapshot.delete(referral.id);   // clear snapshot
  }

  reloadDentalReferralGrid() {
    this.getDentalReferrals().subscribe(() => {
      this.referrals.forEach(r => { if (!r.review) r.review = new DentalReferralFormReview(); });
      // Preserve any active sort; if none, set a default once
      if (!this.lastSort || !this.lastSort.direction) {
        this.lastSort = { active: 'receivedDate', direction: 'desc' } as Sort;
      }
      this.rebindPreservingState();
    });
  }
  // Method to handle cancel action
  onCancel(referral: DentalReferralForm, index: number): void {
    const request = {
      referralId: referral.id,
      referralType: referral.referralType
    };

    this.referralLockService.UnlockReferral(request).subscribe({
      next: (res) => {
        if (res.success && res.referral) {
          // Replace updated referral in the list using referralId
          const index = this.referrals.findIndex(r => r.id === referral.id);
          if (index !== -1) {
            this.referrals[index] = res.referral;
            this.rebindPreservingState();
          }
        } else {
          this.alert.warning(res.message || 'Unable to unlock the referral.');
        }
      },
      error: (err) => {
        console.error('Error unlocking referral:', err);
        this.alert.error('An unexpected error occurred while unlocking the referral.');
      },
      complete: () => {
        this.selectedReferral = null;
        //this.activeRow = null;
        this.isDatePickerChanged = false;
        this.lockSortSnapshot.delete(referral.id);   // clear snapshot
      }
    });
  }


  onAction(action: string) {
    const selectedRows = this.selection.selected;
    if (selectedRows.length === 0) {
      this.alert.warning('No rows selected.');
      this.resetActionDropdown();
      return;
    }

    // Call a method or handle logic based on the selected action
    switch (action) {
      case 'Export':
        this.openExportDialog('selected');
        break;
      case 'Close':
        this.performClose(selectedRows);
        break;
      case 'Open':
        this.performOpen(selectedRows);
        break;
    }

    this.resetActionDropdown();

  }

  private resetActionDropdown(): void {
    setTimeout(() => this.currentActionValue = '', 1);
  }


  performClose(referrals: DentalReferralForm[]) {
    console.log('Closing referrals:', referrals);

    //check for locked referrals
    const lockedReferrals = referrals.filter(referral => referral.review?.isLocked);
    if (lockedReferrals.length > 0) {
      this.alert.warning('One or more selected referrals are locked and cannot be closed.');
      this.resetActionDropdown();
      return;
    }

    //check for referrals without appointment dates
    const referralsWithoutDates = referrals.filter(referral => !referral.review?.appointmentDate);
    if (referralsWithoutDates.length > 0) {
      // Step 2: Prompt the user for confirmation
      const confirmMessage = `
      Some referrals do not have an appointment date.
      Are you sure you want to close them anyway?
    `;
      const userConfirmed = window.confirm(confirmMessage);

      if (!userConfirmed) {
        console.log('User canceled the close operation.');
        this.resetActionDropdown();
        return; // Stop execution if the user does not confirm
      }
    }

    referrals.forEach(referral => {
      if (!referral.review || !referral.review.id) {
        referral.review = new DentalReferralFormReview();
        referral.review.referralId = referral.id;
        referral.review.referralType = referral.referralType;
      }

      referral.review.isClosed = true;
    });

    this.spinner.show();
    const request = new SaveDentalReferralReviewsRequest();
    request.reviews = referrals.map(referral => referral.review);
    
    this.service.SaveDentalReferralReviews(request)
      .pipe(
        finalize(() => {
          // Hide the spinner after the request completes
          this.spinner.hide();
        })
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            //this.getDentalReferrals();
            //this.selection.clear();
            this.alert.success('Referrals updated successfully!');
            this.ngOnInit();

          } else {
            this.alert.warning(response.message);
          }
        },
        error: (error) => {
          console.error('Error submitting referrals:', error);
          this.alert.error('Failed to submit referrals. Please try again.');
        }
      });
    
  }

  performOpen(referrals: DentalReferralForm[]) {
    console.log('Opening referrals:', referrals);

    referrals.forEach(referral => {
      if (!referral.review || !referral.review.id) {
        referral.review = new DentalReferralFormReview();
        referral.review.referralId = referral.id;
        referral.review.referralType = referral.referralType;
      }

      referral.review.isClosed = false;
    });

    this.spinner.show();
    const request = new SaveDentalReferralReviewsRequest();
    request.reviews = referrals.map(referral => referral.review);

    console.log(request);

    this.service.SaveDentalReferralReviews(request)
      .pipe(
        finalize(() => {
          // Hide the spinner after the request completes
          this.spinner.hide();
        })
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.alert.success('Referrals updated successfully!');
            this.ngOnInit();
          } else {
            this.alert.warning(response.message);
          }
        },
        error: (error) => {
          console.error('Error submitting referrals:', error);
          this.alert.error('Failed to submit referrals. Please try again.');
        }
      });
  }

  onSearch() {    
    this.applyFilter();
  }

  onFilterChange() {
    this.applyFilter();
  }

  applyFilter() {
    if (!this.filtersEnabled) {
      this.referralsDataSource.filter = '';
      return;
    }
    const urgentFlag = this.urgentOnly ? '1' : '0';
    const combinedFilter = `${this.currentSearchValue}::${this.currentFilterSelection}::${urgentFlag}`;
    this.referralsDataSource.filter = combinedFilter;

    // keep selection aligned with what the user can see
    this.clampSelectionToFilter();
  }

  clearSearch() {
    this.currentSearchValue = '';
    this.applyFilter();
  }

  clearFilter() {
    this.currentFilterSelection = '';
    this.applyFilter();
  }

  resetAllFilters() {
    this.currentSearchValue = '';
    this.currentFilterSelection = '';
    this.applyFilter();
  }

  // Method to check if any filters are active
  hasActiveFilters(): boolean {
    return (this.currentSearchValue && this.currentSearchValue.length >= 3) || !!this.currentFilterSelection;
  }

  // This method can translate the filter keys into more user-friendly labels if needed
  displayFilterName(filterValue: string): string {
    if (!filterValue) return '';
    if (filterValue.startsWith('NOTE:')) {
      return `DCO Note: ${filterValue.substring(5)}`;
    }
    if (filterValue === 'STATUS:CLOSED') return 'Closed Only';
    if (filterValue === 'STATUS:OPEN') return 'Open Only';

    switch (filterValue) {
      case 'PCP': return 'PCP Only';
      case 'HRAT': return 'HRAT Only';
      case 'Maternity': return 'Maternity Only';
      case 'HasSpokeWith': return 'Has Spoke With';
      case 'HasLeftMessage': return 'Has Left Message';
      case 'HasSentLetter': return 'Has Sent Letter';
      case 'HasScheduledAppointment': return 'Has Scheduled Appointment';
      case 'HasNotes': return 'Has Notes';
      default: return filterValue;
    }
  }


  // Triggered when the appointment date is changed using the datepicker
  onAppointmentDateChange(referral: DentalReferralForm) {
    if (referral.review.appointmentDate) {
      // If a date is selected, set the flag
      this.isDatePickerChanged = true;
    } else {
      // If the date is cleared, automatically reopen the referral
      referral.review.isClosed = false;
    }
  }

  toggleShowClosed(): void {
    this.showClosed = !this.showClosed;
    this.reloadDentalReferralGrid();
  }

  openReferralDetailsDialog(referral: DentalReferralForm) {
    console.log(referral);
    const dialogRef = this.dialog.open(DcoReferralDetailsDialogComponent, {
      data: { referral: referral, showMemberEnrollmentHistory: false, allowMemberUpdates: false, showReferralActivitySection: false, showMemberReferralHistory: true },
      panelClass: 'custom-dialog-panel', // Apply the custom class
      height: '90vh', // Set height as desired
      disableClose: true, // Disable closing the dialog when clicking outside
    })
      .afterClosed()
      .subscribe(response => {
        if (response?.data != null) {
          if (response.data.refresh) {
            this.reloadDentalReferralGrid();
          }
        }
      });
  }

  openReprocessReferralDialog(referral: DentalReferralForm) {
    const dialogRef = this.dialog.open(ReprocessRequestDialogComponent, {
      data: { referral },
      //panelClass: 'custom-dialog-panel', // Apply the custom class
      disableClose: true, // Disable closing the dialog when clicking outside
    })
      .afterClosed()
      .subscribe(response => {
        if (response?.data != null) {
          if (response.data.refresh) {
            this.reloadDentalReferralGrid();
          }
        }
      });
  }

  public onReferralsGridSortChange(sortState: Sort) {
    this.lastSort = sortState;
    if (sortState.active !== 'locked') {
      this.lockSortSnapshot.clear();
    }
    this.rebindPreservingState();
  }

  canForceUnlock(referral: DentalReferralForm): boolean {
    const currentUserId = this.authService.getUserId()?.toLowerCase();
    const lockedByUserId = referral.review?.lockedByUserId?.toLowerCase();

    // Allow if current user is the one who locked the referral
    if (currentUserId && lockedByUserId && currentUserId === lockedByUserId) {
      return true;
    }

    // Or if user has Admin/SuperAdmin role
    return this.authService.getCanForceUnlock();
  }

  getLockTooltip(referral: DentalReferralForm, index: number): string {

    if (this.selectedReferral?.id === referral.id) {
      return 'This referral is currently being edited and cannot be unlocked.';
    } else {

      const currentUserId = this.authService.getUserId()?.toLowerCase();
      const lockedByUserId = referral.review?.lockedByUserId?.toLowerCase();
      const lockedBy = referral.review?.lockedByUser?.fullName ?? 'another user';
      const lockedDate = this.datePipe.transform(referral.review?.lockedDate, 'MM/dd/yyyy h:mm a');

      const isLockedByCurrentUser = currentUserId && lockedByUserId && currentUserId === lockedByUserId;

      if (isLockedByCurrentUser) {
        return `Click to unlock\nYou locked this referral on ${lockedDate}`;
      } else if (this.authService.getCanForceUnlock()) {
        return `Click to force unlock\nLocked by ${lockedBy} on ${lockedDate}`;
      } else {
        return `Locked by ${lockedBy} on ${lockedDate}`;
      }
    }
  }
  

  onLockClick(referral: DentalReferralForm, index: number): void {
    if (!this.canForceUnlock(referral)) return;
    if (this.selectedReferral?.id === referral.id) return;

    this.forceUnlock(referral);
  }

  forceUnlock(referral: DentalReferralForm): void {
    const currentUserId = this.authService.getUserId()?.toLowerCase();
    const lockedByUserId = referral.review?.lockedByUserId?.toLowerCase();
    const lockedByName = referral.review?.lockedByUser?.fullName ?? 'another user';

    const isSelfUnlock = currentUserId === lockedByUserId;

    const title = isSelfUnlock ? 'Unlock Referral' : 'Force Unlock';
    const message = isSelfUnlock
      ? `You locked this referral on ${this.datePipe.transform(referral.review?.lockedDate, 'MM/dd/yyyy h:mm a')}.\nDo you want to unlock it?`
      : `This referral is currently locked by ${lockedByName}.\nAre you sure you want to force unlock it?`;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: title,
        message: message
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;

      const request = {
        referralId: referral.id,
        referralType: referral.referralType
      };

      this.referralLockService.UnlockReferral(request).subscribe({
        next: (res) => {
          if (res.success && res.referral) {
            const index = this.referrals.findIndex(r => r.id === referral.id);
            if (index !== -1) {
              this.referrals[index] = res.referral;
              this.referralsDataSource.data = [...this.referrals];
            }
            const successMsg = isSelfUnlock
              ? 'Referral successfully unlocked.'
              : 'Referral successfully force-unlocked.';
            this.alert.success(successMsg);
          } else {
            this.alert.warning(res.message || 'Unable to unlock the referral.');
          }
        },
        error: (err) => {
          console.error('Error unlocking referral:', err);
          this.alert.error('An error occurred while unlocking the referral.');
        }
      });
    });
  }

  openExportDialog(scope: 'selected' | 'all' = 'selected') {
    // use selected rows if any and scope == 'selected', else use full set
    let baseRows: DentalReferralForm[];

    if (scope === 'selected') {
      const selectedVisible = this.getSelectedFilteredRows();
      if (!selectedVisible.length) {
        this.alert.warning('No rows selected.');
        return;
      }
      baseRows = selectedVisible;
    } else {
      // 'all' → send the whole dataset
      baseRows = this.referrals;
    }

    const data: ExportDialogData = {
      title: 'Export Referrals',
      rows: baseRows,                       // base rows (selected or all)
      dataSource: this.referralsDataSource, // lets dialog honor current filters
      matSort: this.sort,                   // lets dialog honor current sort
      flatten: true,                        // expose review.* as columns
      //restrictToInputRows: scope === 'selected',
      identityKey: 'id',
      // nice default column order (users can still re-order in the dialog)
      presetColumns: [
        //default order


        { key: 'receivedDate', header: 'receivedDate' },
        { key: 'importDate', header: 'importDate' },
        { key: 'referralDate', header: 'referralDate' },
        { key: 'cco', header: 'cco' },
        { key: 'memberMedicaidId', header: 'memberMedicaidId' },
        { key: 'memberFirstName', header: 'memberFirstName' },
        { key: 'memberLastName', header: 'memberLastName' },
        { key: 'memberParentOrGuardianName', header: 'memberParentOrGuardianName' },
        { key: 'memberPhoneNumber', header: 'memberPhoneNumber' },
        { key: 'memberDob', header: 'memberDob' },
        { key: 'memberLanguageSpoken', header: 'memberLanguageSpoken' },

        { key: '$type', header: '$type' },
        { key: 'isUrgent', header: 'isUrgent' },
        { key: 'clinicProgramName', header: 'clinicProgramName' },
        { key: 'clinicNpi', header: 'clinicNpi' },
        { key: 'providerName', header: 'providerName' },
        { key: 'providerPhoneNumber', header: 'providerPhoneNumber' },
        { key: 'providerFaxNumber', header: 'providerFaxNumber' },
        { key: 'providerEmailAddress', header: 'providerEmailAddress' },
        { key: 'submittedBy', header: 'submittedBy' },
        { key: 'dentalCariesOrDecay', header: 'dentalCariesOrDecay' },
        { key: 'swellingOrAbscess', header: 'swellingOrAbscess' },
        { key: 'oralPathology', header: 'oralPathology' },
        { key: 'pain', header: 'pain' },
        { key: 'otherClinicalPresentation', header: 'otherClinicalPresentation' },
        { key: 'otherClinicalPresentationDescription', header: 'otherClinicalPresentationDescription' },
        { key: 'pregnancy', header: 'pregnancy' },
        { key: 'diabetes', header: 'diabetes' },
        { key: 'cardiovascularDisease', header: 'cardiovascularDisease' },
        { key: 'otherMedicalConditions', header: 'otherMedicalConditions' },
        { key: 'otherMedicalConditionsDescription', header: 'otherMedicalConditionsDescription' },
        { key: 'commentsAboutCondition', header: 'commentsAboutCondition' },
        { key: 'memberMailingAddress1', header: 'memberMailingAddress1' },
        { key: 'memberMailingAddress2', header: 'memberMailingAddress2' },
        { key: 'memberMailingCity', header: 'memberMailingCity' },
        { key: 'memberMailingState', header: 'memberMailingState' },
        { key: 'memberMailingZip', header: 'memberMailingZip' },
        { key: 'memberMailingCounty', header: 'memberMailingCounty' },
        { key: 'memberLanguageWritten', header: 'memberLanguageWritten' },
        { key: 'referralType', header: 'referralType' },
        { key: 'submittedOnDate', header: 'submittedOnDate' },
        { key: 'reprocess', header: 'reprocess' },
        { key: 'reprocessReason', header: 'reprocessReason' },
        { key: 'reprocessAdditionalDetails', header: 'reprocessAdditionalDetails' },
        { key: 'review.referralId', header: 'review_referralId' },
        { key: 'review.spokeWithMember', header: 'review_spokeWithMember' },
        { key: 'review.leftMessage', header: 'review_leftMessage' },
        { key: 'review.sentLetter', header: 'review_sentLetter' },
        { key: 'review.scheduledAppointment', header: 'review_scheduledAppointment' },
        { key: 'review.appointmentDate', header: 'review_appointmentDate' },
        { key: 'review.dcoNote', header: 'review_dcoNote' },
        { key: 'review.isLocked', header: 'review_isLocked' },
        { key: 'review.lockedDate', header: 'review_lockedDate' },
        { key: 'review.isClosed', header: 'review_isClosed' },
        { key: 'review.closedDate', header: 'review_closedDate' },
        { key: 'review.referralType', header: 'review_referralType' },
        { key: 'review.notes', header: 'review_notes' },
        { key: 'providerHtrioId', header: 'providerHtrioId' },
        { key: 'importedDco', header: 'importedDco' },
        { key: 'referralReason', header: 'referralReason' },
        { key: 'additionalNotes', header: 'additionalNotes' },
        { key: 'clinic', header: 'clinic' },
        { key: 'questions', header: 'questions' },
        { key: 'providerNpi', header: 'providerNpi' },
        { key: 'patientAwareOfRequest', header: 'patientAwareOfRequest' },
        { key: 'swellingOrAbsess', header: 'swellingOrAbsess' },
        { key: 'comments', header: 'comments' },
        { key: 'dentalCarriesOrDecay', header: 'dentalCarriesOrDecay' },
        { key: 'edd', header: 'edd' },
        { key: 'hasOtherCondition', header: 'hasOtherCondition' },
        { key: 'otherConditions', header: 'otherConditions' },
        { key: 'hasOtherPresentation', header: 'hasOtherPresentation' },
        { key: 'otherPresentation', header: 'otherPresentation' },
        { key: 'dco.dcoName', header: 'dcoName' },

        { key: 'review.closedByUser.fullName', header: 'review_closedByUser' },

        //{ key: 'review.lockedByUser', header: 'review_lockedByUser' },
        //{ key: 'review.lockedByUserId', header: 'review_lockedByUserId' },
        //{ key: 'review.id', header: 'review_id' },
        //{ key: 'dentalCareOrganizationId', header: 'dentalCareOrganizationId' },
        //{ key: 'uniqueId', header: 'uniqueId' },
        //{ key: 'createdByUserId', header: 'createdByUserId' },
        //{ key: 'createdDate', header: 'createdDate' },
        //{ key: 'modifiedByUserId', header: 'modifiedByUserId' },
        //{ key: 'modifiedDate', header: 'modifiedDate' },
        //{ key: 'id', header: 'id' },
        //{ key: 'review.createdByUserId', header: 'review_createdByUserId' },
        //{ key: 'review.createdDate', header: 'review_createdDate' },
        //{ key: 'review.modifiedByUserId', header: 'review_modifiedByUserId' },
        //{ key: 'review.modifiedDate', header: 'review_modifiedDate' },
      ],
      // override headers for any other auto-detected fields
      headerOverrides: {
        memberParentOrGuardianName: 'Parent/Guardian'
      },
      // quick formatters for export file
      accessors: {
        memberPhoneNumber: (r: any) => {
          const digits = (r?.memberPhoneNumber || '').replace(/\D/g, '');
          return digits.length === 10
            ? digits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
            : r?.memberPhoneNumber ?? '';
        },
        submittedOnDate: (r: any) => r?.submittedOnDate
          ? new Date(r.submittedOnDate).toLocaleString()
          : '',
        receivedDate: (r: any) => r?.receivedDate
          ? new Date(r.receivedDate).toLocaleString()
          : '',
        'review.appointmentDate': (r: any) => r?.review?.appointmentDate
          ? new Date(r.review.appointmentDate).toLocaleDateString()
          : ''
      },
      // optionally hide noisy fields if they appear
      excludeKeys: ['review.notes', 'lockedByUser', 'lockedByUserId']
    };

    const ref = this.dialog.open(ExportDialogComponent, {
      width: '65vw',
      maxWidth: '96vw',
      height: '90vh',
      maxHeight: '90vh',
      panelClass: 'dbd-export-dialog',
      disableClose: true,
      data
    });

    ref.keydownEvents().subscribe(e => {
      if (e.key === 'Escape') ref.close();
    })
  }

  private getSelectedFilteredRows(): DentalReferralForm[] {
    const visibleIds = new Set(this.getFilteredRows().map(r => r.id));
    return this.selection.selected.filter(r => visibleIds.has(r.id));
  }

  private asPCP(r: DentalReferralForm): r is PCPReferral {
    return r?.referralType === 'PCP';
  }

  public isUrgent(r: DentalReferralForm): boolean {
    if (!this.asPCP(r)) return false;
    // normalize in case backend sent "true"/1
    const v: any = (r as PCPReferral).isUrgent;
    return v === true || v === 'true' || v === 1 || v === '1';
  }

  public get urgentCount(): number {
    return (this.referrals ?? []).filter(r => this.isUrgent(r)).length;
  }

  public toggleUrgentOnly(): void {
    this.urgentOnly = !this.urgentOnly;
    this.applyFilter(); // will rebuild the combined string (see below)
  }

  private dedupeById(arr: DentalReferralForm[]) {
    const seen = new Map<number, DentalReferralForm>();
    for (const r of arr) seen.set(r.id, r);
    return Array.from(seen.values());
  }

  private getFilteredRows(): DentalReferralForm[] {
    // MatTableDataSource always maintains filteredData (even when no filter)
    return this.referralsDataSource.filteredData ?? this.referralsDataSource.data ?? [];
  }

  filteredCount(): number {
    return this.getFilteredRows().length;
  }

  selectedInFilteredCount(): number {
    const ids = new Set(this.getFilteredRows().map(r => r.id));
    return this.selection.selected.reduce((acc, r) => acc + (ids.has(r.id) ? 1 : 0), 0);
  }

  private clampSelectionToFilter(): void {
    const allowedIds = new Set(this.getFilteredRows().map(r => r.id));
    // Deselect anything that isn't in the current filtered view
    this.selection.selected.slice().forEach(r => {
      if (!allowedIds.has(r.id)) this.selection.deselect(r);
    });
  }

  private getCurrentPageRows(): DentalReferralForm[] {
    const rows = this.getFilteredRows();
    const size = this.paginator?.pageSize ?? rows.length;
    const index = this.paginator?.pageIndex ?? 0;
    const start = index * size;
    const end = start + size;
    return rows.slice(start, end);
  }

  pageCount(): number {
    return this.getCurrentPageRows().length;
  }

  selectedOnPageCount(): number {
    const ids = new Set(this.getCurrentPageRows().map(r => r.id));
    return this.selection.selected.reduce((acc, r) => acc + (ids.has(r.id) ? 1 : 0), 0);
  }

}
